// Import the LitElement base class and html helper function
import { LitElement, html, css, unsafeCSS } from 'lit-element';
import { ifDefined } from 'lit-html/directives/if-defined';
import { unsafeHTML } from 'lit-html/directives/unsafe-html';

import '@oneychatbot/chat-input';
import '@oneychatbot/chat-message';
import '@oneychatbot/chat-buttons';
import '@oneychatbot/prev-links';

/**
 * Chat bot
 *
 * @csspart icon-close
 * @csspart header-title
 * @csspart text
 * @csspart icon
 * @csspart text
 * @csspart messages
 * @csspart icon-send
 *
 * @cssprop [--chat-bot-primary-color=#81bc00] - primary color
 * @cssprop [--chat-bot-primary-color-contrast=#ffffff] - primary color contrast
 * @cssprop [--chat-bot-bubble-width=3rem] - bubble width
 * @cssprop [--chat-bot-bubble-height=3rem] - bubble height
 * @cssprop [--chat-bot-bubble-radius=0] - bubble height
 * @cssprop [--chat-bot-bubble-shadow=none] - bubble shadow
 * @cssprop [--chat-window-width=350px] - bubble width
 * @cssprop [--chat-window-height=500px] - bubble height
 * @cssprop [--chat-window-radius=8px] - bubble radius
 * @cssprop [--chat-window-shadow="0 2px 4px 0 rgba(0, 0, 0, 0.2)"]
 *
 */
export class ChatBot extends LitElement {
  static get properties() {
    return {
      api: { type: String },
      channel: { type: String },
      logo: { type: String },
      errorMessage: { type: String, attribute: 'error-message' },
      opened: { type: Boolean, attribute: 'opened' },
      messages: { type: Array },
      closeImg: { type: String, attribute: 'close-image' },
      minimizeImg: { type: String, attribute: 'minimize-image' },
      sendIcon: { type: String, attribute: 'send-icon' },
      userIcon: { type: String, attribute: 'user-icon' },
      botIcon: { type: String, attribute: 'bot-icon' },
      chatbotIcon: { type: String, attribute: 'chatbot-icon' },
      headerIcon: { type: String, attribute: 'header-tile' },
      textInput: { type: String, attribute: 'text-input' },
      showInfo: { type: Boolean },
      openedPopup: { type: Boolean },
      textPopup: { type: String },
      textBubble: { type: String },
      data: { type: Object, attribute: 'middleware-data' },
    };
  }

  constructor() {
    super();
    this.opened = false;
    this.openedPopup = true;
    this.textPopup =
      'Este servicio almacena de forma anónima tus consultas para ir mejorando las respuestas y aprender a resolver nuevas preguntas.';
    this.botTyping = 'El bot está escribiendo...';
    this.textBubble = 'Hola';
    this.textInput = '';
    this.api = '';
    this.errorMessage = 'Lo sentimos, en estos momentos no podemos atenderle. Inténtelo más tarde.';
    this.messages = [];
    // this.closeImg= new URL('../assets/icon-close.svg', import.meta.url).pathname;
    // this.userIcon = new URL('../assets/avatar.svg', import.meta.url).pathname;
    // this.botIcon = new URL('../assets/icon.svg', import.meta.url).pathname;
    this.minimizeImg = new URL('../assets/icon-minimize.svg', import.meta.url).pathname;
    this.sendIcon = new URL('../assets/icon-send.svg', import.meta.url).pathname;
    this.chatbotIcon = new URL('../assets/oneybot.png', import.meta.url).pathname;
    this.headerIcon = new URL('../assets/logo-header.png', import.meta.url).pathname;
    this.channel = this.constructor.uniqueID();
    this.showInfo = false;
    this.typing = false;
  }

  firstUpdated(props) {
    super.firstUpdated(props);
    const prePatientOnboarding = [
      '¡Buenas! Soy el asistente virtual de Oney.',
      'Cuéntame, ¿qué deseas saber?',
    ];
    const patientOnboarding = [
      '¡Buenas! Soy el asistente virtual de Oney.',
      'Cuéntame, ¿qué deseas saber?',
    ];
    const validation = this.userData1 && parseInt(this.userData1, 10) !== 0;
    const onboarding = validation ? patientOnboarding : prePatientOnboarding;
    this.messages = onboarding.map(message => {
      const obj = {
        content: message,
        type: 'text',
        bot: true,
      };
      return obj;
    });
    this._messages = this.shadowRoot.getElementById('text-container');
    setTimeout(() => this.showInfoTxt(), 4000);
  }

  render() {
    return html`
      <a
        aria-label="Abrir conversación con Chatbot"
        part="bubble-btn"
        class="pointer"
        ?hidden="${this.opened}"
        @click="${this.open}"
      >
        <div part="btn-txt" ?hidden="${!this.showInfo}">
          <slot name="btn-txt"></slot>
          <p>${this.textBubble}</p>
        </div>
        <img
          class="img-btn"
          part="btn-img"
          src="${unsafeCSS(this.chatbotIcon)}"
          alt="Imagen chat"
        />
      </a>

      <div part="window" ?hidden="${!this.opened}">
        ${this.renderHeader()} ${this.renderChatBox()} ${this.renderFooter()}
      </div>
    `;
  }

  renderHeader() {
    return html`
      <div part="header" class="header">
        <div part="header-title" class="header-title">
          <img id="avatarHeader" src="${this.chatbotIcon}" />
          <img id="logoHeader" src="${this.headerIcon}" />
        </div>
        <img title="Close" part="icon-close" src="${this.minimizeImg}" @click="${this.close}" />
      </div>
    `;
  }

  renderChatBox() {
    return html`
      <div part="chat-container">
        <div part="chat-container-upper" id="chat-container-upper"></div>
        <div part="popup-content" ?hidden="${!this.openedPopup}">
          <span part="closePopup" @click="${this.closePopup}">&times;</span>
          <p>${this.textPopup}</p>
        </div>
        <div id="text-container" part="text-container">
          <div
            class="body__wrapper__empty"
            style="-webkit-flex-grow: 2;
            -ms-flex-grow: 2;
            flex-grow: 2;
            -webkit-flex-shrink: 1;
            -ms-flex-shrink: 1;
            flex-shrink: 1;"
          ></div>
          ${this.renderMessages()}
        </div>
      </div>
    `;
  }

  renderMessages() {
    return html`
      <div id="messages" part="messages" tabindex="0" aria-live="polite">
        ${this.messages.map(item => {
          if (item.type === 'text') {
            return html`
              <chat-message
                exportparts="icon text"
                ?bot=${this.constructor.isBot(item)}
                ?proactive=${item.proactivity}
              >
                ${this.constructor.isBot(item) ? unsafeHTML(item.content) : item.content}
              </chat-message>
            `;
          }
          if (item.type === 'link' && item.content && item.content.pageUrl) {
            return html`
              <prev-links
                exportparts="icon text"
                ?bot=${this.constructor.isBot(item)}
                ?proactive=${item.proactivity}
                imgUrl=${item.content.imgUrl || ''}
                pageUrl=${item.content.pageUrl}
                title=${item.content.title || ''}
                description=${item.content.description || ''}
              >
              </prev-links>
            `;
          }
          if (item.type === 'buttons') {
            return html`
              <chat-buttons
                exportparts="icon text"
                ?bot=${this.constructor.isBot(item)}
                botones=${JSON.stringify(item.content)}
                actionType=${item.actionType}
                @submit="${this._addText}"
              >
              </chat-buttons>
            `;
          }
          if (item.type === 'typing') {
            return html` <p part="saving">${this.botTyping}</p> `;
          }
          return html``;
        })}
      </div>
    `;
  }

  renderFooter() {
    return html`
      <div part="footer">
        <chat-input
          exportparts="container icon-send"
          .value="${this.textInput}"
          icon="${ifDefined(this.sendIcon)}"
          @submit="${this._addText}"
          @microAction="${this._microAction}"
        ></chat-input>
      </div>
    `;
  }

  async request(data) {
    const body = data;
    body.midData = this.data || {};
    body.channel = this.channel;
    body.type = 'oney_web';
    return fetch(this.api, {
      method: 'POST',
      body: JSON.stringify(body),
      headers: {
        'Content-Type': 'application/json',
      },
    })
      .then(res => res.json())
      .then(res => {
        this._typingMessage(true);
        setTimeout(() => {
          this._typingMessage(false);
          this.parseResponse(res, false);
        }, 1500);
      })
      .catch(err => this.parseError(err));
  }

  parseResponse(response) {
    const answers = response.answers || [{ content: response.text }];
    answers.forEach(answer => {
      if (answer.type !== 'buttons' && answer.type !== 'link') {
        const text = answer.content;
        /* const text = this.constructor.messageReplacements.reduce(
          (acc, cur) => acc.replace(cur.regexp, cur.replace),
          answer.content,
        ); */
        this._addMessage(text, answer.type, true);
      } else {
        this._addMessage(answer.content, answer.type, true, answer.actionType);
      }
    });
  }

  parseError(error) {
    // eslint-disable-next-line no-console
    console.error(error);
    this._addMessage(this.errorMessage, 'text', true);
  }

  _addMessage(content, type, bot = false, actionType) {
    // Replace regexps in message

    // const text = this.constructor.messageReplacements.reduce(
    //   (acc, cur) => acc.replace(cur.regexp, cur.replace),
    //   message,
    // );
    this.messages = [...this.messages, { bot, content, type, actionType }];
  }

  _typingMessage(status) {
    if (status && !this.typing) {
      this.typing = true;
      this.messages = [...this.messages, { bot: true, type: 'typing' }];
    } else if (!status && this.typing) {
      this.messages.pop();
      this.typing = false;
    }
  }

  async _addText(evt) {
    const { text } = evt.detail;
    this._typingMessage(false);
    this._userMessageTop = this._messages.scrollHeight;
    /* istanbul ignore else */
    if (text) {
      this._addMessage(text, 'text');
      this.request({ text });
    }
    if (!text && evt.detail && evt.detail.title) {
      const { title, data } = evt.detail;
      this._addMessage(title, 'text');
      this.request(data);
    }
  }

  static isBot(item) {
    return ifDefined(item.bot) && item.bot;
  }

  updated(changedProperties) {
    super.updated(changedProperties);
    if (changedProperties.has('messages')) {
      this.updateComplete.then(() => {
        setTimeout(() => {
          const lastMessage = this.messages[this.messages.length - 1];
          const isBotMessage = lastMessage.bot;
          if (isBotMessage && lastMessage.type !== 'typing') {
            if (!lastMessage.proactivity) {
              const isFirstUserInteraction = this.messages.filter(m => !m.bot).length === 1;
              if (!isFirstUserInteraction) {
                this._messages.scrollTop = this._userMessageTop;
              } else {
                const userMessages = Array.from(
                  this._messages.querySelectorAll('chat-message'),
                ).filter(c => !c.bot);
                this._messages.scrollTop = this._userMessageTop - userMessages[0].offsetHeight;
              }
            }
          } else {
            this._messages.scrollTop = this._messages.scrollHeight;
          }
        });
      });
    }
  }

  _microAction(data) {
    const display = data.detail.on ? 'inline' : 'none';
    this.shadowRoot.getElementById('chat-container-upper').style.display = display;
  }

  close() {
    this.opened = false;
  }

  open() {
    this.opened = true;
    this.showInfo = false;
  }

  showInfoTxt() {
    this.showInfo = true;
  }

  openPopup() {
    this.openedPopup = true;
  }

  closePopup() {
    this.openedPopup = false;
  }

  static uniqueID(length = 10) {
    return Math.random().toString(36).substr(2, length);
  }

  static get messageReplacements() {
    return [
      // Monospace
      {
        regexp: /```(.*?)```/g,
        replace: '<code>$1</code>',
      },
      // Start line
      {
        regexp: /^/,
        replace: '<p>',
      },
      // End line
      {
        regexp: /$/,
        replace: '</p>',
      },
      // New Lines
      {
        regexp: /(\n)+\s*/gim,
        replace: '</p><p>',
      },
    ];
  }

  static get styles() {
    return css`
      :host {
        font-family: 'Open Sans', sans-serif;
      }

      :host([hidden]),
      [hidden] {
        display: none !important;
      }

      p {
        margin: 0;
      }

      p + p {
        margin-top: 0.5rem;
      }

      #characterImage {
        width: 100%;
        position: absolute;
        bottom: 0;
        left: 0;
      }

      [part='bubble-btn'] {
        cursor: pointer;
        position: fixed;
        bottom: 2rem;
        right: 2rem;
        border: none;
        display: flex;
        align-items: center;
        height: var(--chat-bot-bubble-height, 3rem);
        z-index: 101;
      }

      [part='btn-img'] {
        width: var(--chat-bot-bubble-width, 3rem);
        height: var(--chat-bot-bubble-height, 3rem);
        border-radius: var(--chat-bot-bubble-radius, 100%);
        box-shadow: var(--chat-bot-bubble-shadow, none);
        animation: shake 1.5s cubic-bezier(0.36, 0.07, 0.19, 0.97) both 3s;
        transform: translate3d(0, 0, 0);
        backface-visibility: hidden;
        perspective: 1000px;
        will-change: transform;
      }

      [part='btn-img'].shake {
        animation: shake 0.82s cubic-bezier(0.36, 0.07, 0.19, 0.97) both;
        transform: translate3d(0, 0, 0);
        backface-visibility: hidden;
        perspective: 1000px;
      }

      [part='btn-txt'] {
        padding: var(--chat-bot-message-padding, 1rem);
        background-color: var(--chat-bot-message-background, #f3f7f9);
        box-shadow: 0 2px 4px 0 rgba(0, 0, 8, 0.2);
        color: var(--chat-bot-message-color, #333);
        border-radius: var(--chat-bot-message-radius, 10px 10px 0 10px);
        margin-right: 1rem;
        animation: fade-in-right 0.5s cubic-bezier(0.39, 0.575, 0.565, 1) both;
      }

      * > ::slotted([slot='btn-txt']) {
        margin: 0;
        font-size: var(--chat-bot-message-font-size, 1rem);
        word-break: break-word;
      }

      [part='window'] {
        z-index: 101;
        position: fixed;
        bottom: 0;
        height: 100%;
        right: 0;
        width: 100%;
        top: 0;
        max-height: none;
        max-width: none;
        display: flex;
        flex-direction: column;
        overflow: hidden;
        background: var(--chat-bot-primary-color-contrast, #fff);
        color: var(--chat-bot-primary-color, #006699);
      }

      [part='messages']:focus {
        outline: none;
      }

      [part='chat-container'] {
        overflow-y: inherit;
        height: 100%;
      }

      [part='chat-container-upper'] {
        display: none;
        box-sizing: border-box;
        position: absolute;
        background-color: black;
        opacity: 0.5;
        z-index: 101;
        height: 100%;
        width: 100%;
      }

      [part='text-container'] {
        float: left;
        flex-direction: column;
        overflow-y: scroll;
        height: 100%;
        overflow: auto;
        display: -ms-flexbox;
        display: -webkit-flex;
        display: flex;
        -webkit-flex-direction: column;
        -ms-flex-direction: column;
        -webkit-flex-grow: 2;
        -ms-flex-grow: 2;
        flex-grow: 2;
        overflow-x: hidden;
        z-index: 10;
      }

      [part='avatar-container'] {
        position: relative;
        display: inline-block;
        float: left;
        flex-direction: column;
        height: 100%;
      }

      .header {
        align-items: center;
        padding: 0.5rem 1rem 0.5rem 0;

        flex: 0 0 auto;
        justify-content: space-between;
        display: flex;
        background: var(--chat-bot-primary-color, #006699);
        color: var(--chat-bot-primary-color-contrast, #fff);
        height: 1rem;
      }

      [part='icon-close'] {
        cursor: pointer;
        width: 1rem;
        height: 2rem;
      }

      .header-title {
        display: flex;
        align-items: center;
        height: 2rem;
      }

      #avatarHeader {
        max-height: 2rem;
        display: none;
      }

      #logoHeader {
        max-height: 1rem;
        padding: 0.5rem;
      }

      [part='footer'] {
        height: 2rem;
        flex: 0 0 auto;
      }

      chat-message:not([bot]) {
        flex-direction: row-reverse;
        --chat-message-radius: 8px 8px 0 8px;
      }

      [part='popup-content'] {
        font-family: sans-serif;
        box-sizing: border-box;
        position: absolute;
        background-color: #edf7f6;
        opacity: 0.9;
        z-index: 102;
        padding: 10px;
        border: 0.5px solid #d5dbdb;
        width: 100%;
        font-size: 12px;
      }

      /* The Close Button */
      [part='closePopup'] {
        color: #aaaaaa;
        padding-right: 10px;
        float: right;
        font-size: 28px;
        font-weight: bold;
      }

      [part='closePopup']:hover,
      [part='closePopup']:focus {
        color: #000;
        text-decoration: none;
        cursor: pointer;
      }

      [part='saving'] {
        padding: 4px 6px;
        font-size: 10px;
        font-style: italic;
        color: #9e9ea1;
      }

      [part='text-container']::-webkit-scrollbar {
        width: 5px;
      }

      [part='text-container']::-webkit-scrollbar-track {
        background: #f1f1f1;
      }

      [part='text-container']::-webkit-scrollbar-thumb {
        background: #ced3d6;
        border-radius: 10px;
      }

      [part='text-container']::-webkit-scrollbar-thumb:hover {
        background: #888;
      }

      @media only screen and (max-width: 600px) {
        #avatarHeader {
          display: inline;
        }
        [part='text-container'] {
          width: 100%;
        }
      }

      @media (min-width: 600px) {
        [part='window'] {
          top: auto;
          left: auto;
          bottom: 20px;
          right: 2%;
          width: var(--chat-window-width, 400px);
          height: var(--chat-window-height, 350px);
          border-radius: var(--chat-window-radius, 8px);
          box-shadow: var(--chat-window-shadow, 0 2px 4px 0 rgba(0, 0, 0, 0.2));
        }
        [part='text-container'] {
          width: 100%;
        }
      }

      @media (min-width: 1200px) {
        [part='window'] {
          top: auto;
          left: auto;
          bottom: 20px;
          right: 2%;
          width: var(--chat-window-width, 350px);
          height: var(--chat-window-height, 420px);
          border-radius: var(--chat-window-radius, 8px);
          box-shadow: var(--chat-window-shadow, 0 2px 4px 0 rgba(0, 0, 0, 0.2));
        }
        [part='text-container'] {
          width: 100%;
        }
      }

      @keyframes shake {
        10%,
        90% {
          transform: translate3d(-1px, 0, 0);
        }

        20%,
        80% {
          transform: translate3d(2px, 0, 0);
        }

        30%,
        50%,
        70% {
          transform: translate3d(-4px, 0, 0);
        }

        40%,
        60% {
          transform: translate3d(4px, 0, 0);
        }
      }

      @keyframes fade-in-right {
        0% {
          -webkit-transform: translateX(50px);
          transform: translateX(50px);
          opacity: 0;
        }
        100% {
          -webkit-transform: translateX(0);
          transform: translateX(0);
          opacity: 1;
        }
      }
    `;
  }
}
